import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Building2 } from "lucide-react";
import PlayfulNavbar from "../components/playful/PlayfulNavbar";
import PlayfulFooter from "../components/playful/PlayfulFooter";
import { CandyButton } from "../components/playful/CandyButton";
import { PopInput } from "../components/playful/PopInput";
import { PasswordInput } from "../components/playful/PasswordInput";
import { StickerCard } from "../components/playful/StickerCard";
import { GeometricBadge } from "../components/playful/GeometricBadge";
import { FloatingIcon } from "../components/playful/FloatingIcon";
import { Blob, ConfettiDecoration } from "../components/playful/Decor";
import { GoogleIcon, AppleIcon } from "../components/playful/SocialIcons";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, API_BASE } from "../lib/api";

const APPLE_ENABLED = process.env.REACT_APP_OAUTH_APPLE_ENABLED === "true";

// Full-page redirect to the backend, which 302s to the provider. Carries the
// stored referral code so social signups still attribute.
function startOauth(provider) {
  const origin = API_BASE.replace(/\/api$/, "");
  const ref = localStorage.getItem("linklane_ref") || "";
  const qs = new URLSearchParams({ return_to: "/dashboard" });
  if (ref) qs.set("ref_code", ref);
  window.location.href = `${origin}/api/auth/oauth/${provider}/start?${qs}`;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  // Two-factor step: shown after the backend answers a correct password with
  // 401 "mfa_required". The user then enters a TOTP or recovery code.
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [ssoBusy, setSsoBusy] = useState(false);

  // Surface SSO callback failures redirected back as ?sso_error=<reason>.
  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get("sso_error");
    if (reason) {
      toast.error(`Single sign-on failed: ${reason.replace(/_/g, " ")}`);
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  // Route the entered email to its org IdP if the domain has SSO configured.
  const onSso = async () => {
    const addr = email.trim().toLowerCase();
    if (!addr || !addr.includes("@")) {
      setFormError("Enter your work email first, then continue with SSO.");
      return;
    }
    setSsoBusy(true);
    setFormError("");
    try {
      const { data } = await api.post("/auth/sso/discover", { email: addr });
      if (data.sso && data.login_url) {
        // Full-page nav to the backend, which 302-redirects to the IdP.
        const origin = API_BASE.replace(/\/api$/, "");
        window.location.href = `${origin}${data.login_url}`;
      } else {
        setFormError("No single sign-on is configured for that email domain.");
      }
    } catch (err) {
      setFormError(formatApiError(err));
    } finally {
      setSsoBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError("");
    try {
      await login(email.trim().toLowerCase(), password, mfaStep ? mfaCode.trim() : undefined);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "mfa_required") {
        setMfaStep(true); // password OK — prompt for the second factor
        setFormError("");
      } else if (detail === "password_login_unavailable") {
        setFormError("This account uses Google or Apple sign-in — use the buttons below.");
      } else {
        setFormError(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink">
      <PlayfulNavbar />
      <section className="relative mx-auto max-w-[1400px] px-5 pb-20 pt-10 md:px-10 md:pb-32 md:pt-16">
        <Blob
          tone="yellow"
          size={340}
          className="absolute -left-24 -top-24 hidden animate-float opacity-70 md:block"
        />
        <Blob
          tone="pink"
          size={220}
          className="absolute right-0 top-32 hidden animate-drift opacity-70 md:block"
        />
        <div className="relative grid grid-cols-1 items-center gap-8 md:grid-cols-12 md:gap-10">
          <div className="order-2 md:order-1 md:col-span-6 md:pr-10">
            <GeometricBadge tone="candy">LINKLANE — SIGN IN</GeometricBadge>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl md:text-7xl md:leading-[0.95]">
              Welcome back.
              <span className="block text-candy">Your lanes are waiting.</span>
            </h1>
            <p className="mt-5 max-w-md font-sans text-base md:mt-6 md:text-lg">
              Sign in to access your dashboard, QR codes, and the full LinkLane analytics — all the
              clicks, none of the enterprise-y noise.
            </p>
            <div className="mt-8 flex gap-4">
              <FloatingIcon icon={LogIn} tone="candy" size={60} rotate={-8} />
              <FloatingIcon icon={LogIn} tone="yellow" size={44} rotate={12} />
            </div>
          </div>
          <div className="order-1 min-w-0 md:order-2 md:col-span-5 md:col-start-8">
            <StickerCard
              tone="candy"
              bg="paper"
              hover={false}
              className="relative p-6 sm:p-8 md:p-10"
            >
              <ConfettiDecoration className="opacity-30" density="sparse" />
              <form
                onSubmit={onSubmit}
                className="relative flex flex-col gap-6"
                data-testid="login-form"
              >
                <PopInput
                  type="email"
                  required
                  label="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setFormError("");
                  }}
                  placeholder="you@linklane.app"
                  autoFocus
                  data-testid="login-email-input"
                />
                <div>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                      Password
                    </span>
                    <Link
                      to="/forgot"
                      className="font-mono text-2xs uppercase tracking-[0.22em] text-candy underline-offset-4 hover:underline"
                      data-testid="forgot-link"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <PasswordInput
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormError("");
                    }}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                {mfaStep && (
                  <PopInput
                    required
                    label="Two-factor code"
                    value={mfaCode}
                    onChange={(e) => {
                      setMfaCode(e.target.value);
                      setFormError("");
                    }}
                    placeholder="123456 or recovery code"
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoFocus
                    data-testid="login-mfa-input"
                  />
                )}
                {formError && (
                  <p className="-mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-pink">
                    {formError}
                  </p>
                )}
                {mfaStep && (
                  <p className="-mt-2 font-mono text-[11px] tracking-[0.04em] text-muted-foreground">
                    Enter the 6-digit code from your authenticator app, or one of your recovery
                    codes.
                  </p>
                )}
                <CandyButton
                  type="submit"
                  variant="candy"
                  size="lg"
                  block
                  disabled={busy}
                  withArrow
                  data-testid="login-submit-btn"
                >
                  {busy
                    ? mfaStep
                      ? "Verifying…"
                      : "Signing in…"
                    : mfaStep
                      ? "Verify code"
                      : "Sign in"}
                </CandyButton>
                {!mfaStep && (
                  <>
                    <div className="flex items-center gap-3">
                      <span className="h-px flex-1 bg-line" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                        or
                      </span>
                      <span className="h-px flex-1 bg-line" />
                    </div>
                    <CandyButton
                      type="button"
                      variant="paper"
                      size="lg"
                      block
                      icon={GoogleIcon}
                      onClick={() => startOauth("google")}
                      data-testid="login-google-btn"
                    >
                      Continue with Google
                    </CandyButton>
                    {APPLE_ENABLED && (
                      <CandyButton
                        type="button"
                        variant="ink"
                        size="lg"
                        block
                        icon={AppleIcon}
                        onClick={() => startOauth("apple")}
                        data-testid="login-apple-btn"
                      >
                        Continue with Apple
                      </CandyButton>
                    )}
                    <CandyButton
                      type="button"
                      variant="paper"
                      size="lg"
                      block
                      icon={Building2}
                      disabled={ssoBusy}
                      onClick={onSso}
                      data-testid="login-sso-btn"
                    >
                      {ssoBusy ? "Checking…" : "Continue with SSO"}
                    </CandyButton>
                  </>
                )}
                <div className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  NEW TO LINKLANE?{" "}
                  <Link
                    to="/signup"
                    className="text-candy underline-offset-4 hover:underline"
                    data-testid="login-go-signup"
                  >
                    GET STARTED
                  </Link>
                </div>
              </form>
            </StickerCard>
          </div>
        </div>
      </section>
      <PlayfulFooter />
    </div>
  );
}
